import { getSGRWarrantyRequests } from 'lib/api/sgr';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import WarrantiesTable from 'lib/common/Tables/WarrantiesTable';
import { SGR_STATES_FILTERS, SGR_TYPES } from 'lib/consts';
import { CustomButtonProps } from 'lib/designSystem/HeaderWithActions';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { errorHandler } from 'lib/functions/errors';
import { validatePermissions } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import CreateWarrantyDialog from './components/CreateWarrantyDialog';
import WarrantyDetailDialog from './components/WarrantyDetailDialog';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

type StateFilter = (typeof SGR_STATES_FILTERS)[number]['value'];

const SGR = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [stateFilter, setStateFilter] = useState<StateFilter>('active');
  const [executive, setExecutive] = useState<string | null>(null);
  const [selectedWarranty, setSelectedWarranty] = useState<Warranty | null>(
    null,
  );

  // Filter states
  const [type, setType] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [requestId, setRequestId] = useState<number | null>(null);
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [principalRut, setPrincipalRut] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const generateQuery = (params?: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    const query = new URLSearchParams();
    const { page = pagination.page } = params ?? {};
    const { pageSize = pagination.pageSize } = params ?? {};
    const { exportComplete } = params ?? { exportComplete: false };
    query.append('page', page.toString());
    query.append('pageSize', pageSize.toString());
    if (sortBy) query.append('sortBy', sortBy);
    if (sortOrder) query.append('sortOrder', sortOrder);
    if (principalRut) query.append('principalRut', principalRut);
    if (exportComplete) query.append('exportComplete', 'true');
    if (identifier) query.append('businessIdentifier', identifier);
    if (requestId) query.append('warrantyRequestId', requestId.toString());
    if (executive) query.append('executive', executive);
    if (type) query.append('type', type);
    if (stateFilter) {
      const filter = SGR_STATES_FILTERS.find(
        (filter) => filter.value === stateFilter,
      );
      if (filter) query.append('status', filter.states.join(','));
    }
    return query.toString();
  };

  const fetchWarranties = async (params?: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    try {
      setIsLoading(true);
      const query = generateQuery(params);
      const response = await getSGRWarrantyRequests(dispatch, query);
      if (!params?.exportComplete) {
        setWarranties(response.data);
        setPagination(response.pagination);
      }
      if (response && selectedWarranty) {
        const updatedWarranty = response.data.find(
          (w: Warranty) => w.id === selectedWarranty.id,
        );
        if (updatedWarranty) setSelectedWarranty(updatedWarranty);
        else setSelectedWarranty(null);
      }
      return response;
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStateFilterChange = (value: string) => {
    setStateFilter(value as StateFilter);
  };

  const handleOpenWarrantyDetail = (warrantyId: number) => {
    const warranty = warranties.find((warranty) => warranty.id === warrantyId);
    if (!warranty) return;
    setSelectedWarranty(warranty);
    setOpenDetailDialog(true);
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'ID solicitud',
      value: requestId,
      setter: setRequestId,
    },
    {
      label: 'Cliente',
      type: 'autocomplete',
      value: identifier,
      key: 'business',
      setter: setIdentifier,
    },
    {
      type: 'autocomplete',
      label: 'Mandante',
      value: principalRut,
      key: 'business',
      setter: setPrincipalRut,
    },
    {
      type: 'select',
      label: 'Tipo',
      value: type,
      setter: setType,
      options: [{ label: 'Todos', value: '' }, ...SGR_TYPES],
    },
    ...(validatePermissions(user, 'businesses_read_all')
      ? [
          {
            label: 'Ejecutivos',
            type: 'autocomplete' as const,
            key: 'userName',
            value: executive,
            setter: setExecutive,
            options: [],
          },
        ]
      : []),
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={fetchWarranties}
          selectedSortBy={sortBy}
          setSelectedSortBy={setSortBy}
          sortOptions={[{ value: 'createdAt', label: 'Creación' }]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      name: 'Nueva Garantía',
      color: 'primary',
      action: () => setOpenCreateDialog?.(true),
    },
  ];

  useEffect(() => {
    void fetchWarranties();
  }, [stateFilter]);

  return (
    <>
      <WarrantiesTable
        warranties={warranties}
        setWarranties={setWarranties}
        handleOpenWarrantyDetail={handleOpenWarrantyDetail}
        loading={isLoading}
        pagination={pagination}
        setPagination={setPagination}
        fetchWarranties={fetchWarranties}
        setIsLoading={setIsLoading}
        buttons={buttons}
        stateFilter={stateFilter}
        stateFilterComponent={
          <CustomToggleButtonGroup
            options={SGR_STATES_FILTERS}
            value={stateFilter}
            onChange={handleStateFilterChange}
          />
        }
      />

      {openCreateDialog && (
        <CreateWarrantyDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          fetchWarranties={() => void fetchWarranties()}
        />
      )}
      {openDetailDialog && (
        <WarrantyDetailDialog
          open={openDetailDialog}
          onClose={() => setOpenDetailDialog(false)}
          warranty={selectedWarranty}
          refreshWarranty={() => void fetchWarranties()}
        />
      )}
    </>
  );
};

export default SGR;
